import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Avatar,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Switch,
  FormGroup,
  Slider,
  Button,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  InputLabel,
  Stack,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import { WATERMARK_API_URL, AVATARS_BUCKET } from "../../config";

const PRESETS = [
  { value: "clean_diagonal", label: "Clean Diagonal" },
  { value: "tile", label: "Tile" },
  { value: "bottom_right", label: "Bottom Right" },
];

const ACCEPTED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ACCEPTED_EXTENSIONS = /\.(jpe?g|png|webp|gif)$/i;

function isSupportedImage(file) {
  if (!file) return false;
  if (file.type && ACCEPTED_MIME_TYPES.includes(file.type)) return true;
  return ACCEPTED_EXTENSIONS.test(file.name || "");
}

function decodeJwt(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function resolveDisplayName(user, token) {
  if (user && typeof user === "object") {
    if (user.username) return user.username;
    if (user.name) return user.name;
    if (user.email) return user.email;
  }
  const claims = decodeJwt(token) || {};
  return (
    claims.preferred_username ||
    claims.name ||
    claims["cognito:username"] ||
    claims.email ||
    ""
  );
}

const DEFAULT_CONFIG = (displayName) => ({
  applyTo: { preview: true, cover: true, thumbnail: true },
  text: displayName || "Protected by ShieldShot",
  preset: "clean_diagonal",
  subjectShield: true,
  bufferPx: 24,
  opacity: 0.5,
  density: 25,
  badgeRemoval: false,
  logoKey: null,
  version: 0,
  updatedAt: null,
});

function SettingsWatermark({ token, user }) {
  const displayName = useMemo(() => resolveDisplayName(user, token), [user, token]);
  const initialConfig = useMemo(() => DEFAULT_CONFIG(displayName), [displayName]);
  const profileLogoUrl = useMemo(() => {
    const sub = user?.sub || (decodeJwt(token) || {}).sub;
    if (!sub || !AVATARS_BUCKET) return null;
    return `https://${AVATARS_BUCKET}.s3.us-east-1.amazonaws.com/${sub}.png`;
  }, [user, token]);
  const [config, setConfig] = useState(initialConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFileName, setPreviewFileName] = useState("");
  const [previewBase64, setPreviewBase64] = useState(null);
  const [previewGenerating, setPreviewGenerating] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(null);
  const [previewHeight, setPreviewHeight] = useState(null);
  const [previewJobId, setPreviewJobId] = useState(null);
  const [previewPolling, setPreviewPolling] = useState(false);
  const [completedJobId, setCompletedJobId] = useState(null);  // last successful async job (for rerender)
  const previewInputRef = useRef(null);
  const previewDebounceRef = useRef(null);
  const previewAbortRef = useRef(null);
  const previewPollAbortRef = useRef(null);
  const previewPollRef = useRef(null);
  const previewPollingRef = useRef(false);

  const fetchConfig = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      const res = await fetch(`${WATERMARK_API_URL}/v1/distributor/default-profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load watermark settings: ${res.status}`);
      const data = await res.json();
      setConfig((prev) => ({ ...initialConfig, ...prev, ...data }));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [token, initialConfig]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const renderPreview = useCallback(async (base64, currentConfig) => {
    if (!base64) return;
    // Cancel any in-flight preview request or polling so a slow earlier response
    // can't overwrite a newer one (debounced re-render race).
    if (previewAbortRef.current) previewAbortRef.current.abort();
    if (previewPollAbortRef.current) previewPollAbortRef.current.abort();
    if (previewPollRef.current) {
      clearTimeout(previewPollRef.current);
      previewPollRef.current = null;
    }

    const controller = new AbortController();
    previewAbortRef.current = controller;
    let asyncPath = false;
    try {
      setPreviewGenerating(true);
      setPreviewJobId(null);
      setPreviewPolling(false);
      const res = await fetch(`${WATERMARK_API_URL}/v1/distributor/preview`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          imageBase64: base64,
          settings: {
            subjectShield: currentConfig.subjectShield,
            text: currentConfig.text,
            preset: currentConfig.preset,
            bufferPx: currentConfig.bufferPx,
            badgeRemoval: currentConfig.badgeRemoval,
            opacity: currentConfig.opacity,
            density: currentConfig.density,
          },
        }),
        signal: controller.signal,
      });
      if (res.status === 202) {
        // Async path — subjectShield is enabled, start polling.
        // Mark asyncPath BEFORE calling startPolling so the finally block
        // does not null out previewAbortRef (polling needs it for its
        // stale-request guard).
        asyncPath = true;
        const data = await res.json().catch(() => ({}));
        if (previewAbortRef.current !== controller) {
          setPreviewPolling(false);
          return;
        }
        setPreviewJobId(data.previewJobId || null);
        setPreviewPolling(true);
        startPolling(data.previewJobId, token, controller);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `Preview failed: ${res.status}`);
      }
      const data = await res.json();
      // If a newer request started while we were in-flight, ignore this response.
      if (previewAbortRef.current !== controller) return;
      setPreviewUrl(data.resultUrl);
      setPreviewWidth(data.width ?? null);
      setPreviewHeight(data.height ?? null);
    } catch (err) {
      if (err.name === "AbortError") return;
      if (previewAbortRef.current !== controller) return;
      setError(err.message);
      setPreviewUrl(null);
    } finally {
      if (previewAbortRef.current === controller && !asyncPath) {
        previewAbortRef.current = null;
        setPreviewGenerating(false);
      }
    }
  }, [token]);

  // Mirror previewPolling into a ref so renderPreview's finally can read it without
  // needing to add it to the dep array.
  useEffect(() => {
    previewPollingRef.current = previewPolling;
  }, [previewPolling]);

  const startPolling = useCallback((jobId, authToken, parentController) => {
    const pollController = new AbortController();
    previewPollAbortRef.current = pollController;
    const poll = async () => {
      try {
        const res = await fetch(`${WATERMARK_API_URL}/v1/distributor/preview/${jobId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
          signal: pollController.signal,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error?.message || `Poll failed: ${res.status}`);
        }
        const data = await res.json();
        if (previewPollAbortRef.current !== pollController) return;
        if (previewAbortRef.current !== parentController) return;
        if (data.status === "READY") {
          setPreviewUrl(data.resultUrl);
          setPreviewWidth(data.width ?? null);
          setPreviewHeight(data.height ?? null);
          setPreviewPolling(false);
          setPreviewGenerating(false);
          setCompletedJobId(jobId);  // cache for re-render on slider changes
          setPreviewJobId(null);
          return;
        }
        if (data.status === "FAILED") {
          setError(data.failReason || "Subject Shield preview failed. Try again or disable Subject Shield.");
          setPreviewUrl(null);
          setPreviewPolling(false);
          setPreviewGenerating(false);
          setPreviewJobId(null);
          return;
        }
        // PENDING or DETECTING — continue polling after 2s delay.
        previewPollRef.current = setTimeout(poll, 2000);
      } catch (err) {
        if (err.name === "AbortError") return;
        if (previewPollAbortRef.current !== pollController) return;
        if (previewAbortRef.current !== parentController) return;
        setError(err.message);
        setPreviewPolling(false);
        setPreviewGenerating(false);
        setPreviewJobId(null);
      }
    };
    poll();
  }, []);

  const handlePreviewFile = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!isSupportedImage(file)) {
      setError("Unsupported file format. Please upload JPEG, PNG, WebP, or GIF. (HEIC is not supported in browser preview — convert to JPEG first.)");
      if (previewInputRef.current) previewInputRef.current.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPreviewBase64(dataUrl);
      setPreviewFileName(file.name);
      setCompletedJobId(null);
      setError(null);
    };
    reader.onerror = () => {
      setError("Failed to read the selected file.");
      if (previewInputRef.current) previewInputRef.current.value = "";
    };
    reader.readAsDataURL(file);
  }, []);

  const handleClearPreview = useCallback(() => {
    if (previewDebounceRef.current) {
      clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = null;
    }
    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
      previewAbortRef.current = null;
    }
    if (previewPollAbortRef.current) {
      previewPollAbortRef.current.abort();
      previewPollAbortRef.current = null;
    }
    if (previewPollRef.current) {
      clearTimeout(previewPollRef.current);
      previewPollRef.current = null;
    }
    setPreviewUrl(null);
    setPreviewBase64(null);
    setPreviewFileName("");
    setPreviewWidth(null);
    setPreviewHeight(null);
    setPreviewGenerating(false);
    setPreviewPolling(false);
    setPreviewJobId(null);
    setCompletedJobId(null);
    if (previewInputRef.current) previewInputRef.current.value = "";
  }, []);

  const rerenderPreview = useCallback(async (jobId, currentConfig, token) => {
    if (!jobId) return;
    if (previewPollAbortRef.current) previewPollAbortRef.current.abort();
    if (previewPollRef.current) {
      clearTimeout(previewPollRef.current);
      previewPollRef.current = null;
    }

    try {
      setPreviewGenerating(true);
      const res = await fetch(`${WATERMARK_API_URL}/v1/distributor/preview/${jobId}/rerender`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          settings: {
            subjectShield: currentConfig.subjectShield,
            text: currentConfig.text,
            preset: currentConfig.preset,
            bufferPx: currentConfig.bufferPx,
            badgeRemoval: currentConfig.badgeRemoval,
            opacity: currentConfig.opacity,
            density: currentConfig.density,
          },
        }),
      });
      if (!res.ok) {
        // If the job expired or was cleaned up, fall back to full preview.
        if (res.status === 404 || res.status === 400) {
          setCompletedJobId(null);
          renderPreview(previewBase64, currentConfig);
          return;
        }
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `Re-render failed: ${res.status}`);
      }
      const data = await res.json();
      setPreviewUrl(data.resultUrl);
      setPreviewWidth(data.width ?? null);
      setPreviewHeight(data.height ?? null);
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err.message);
    } finally {
      setPreviewGenerating(false);
    }
  }, [token, previewBase64, renderPreview]);

  // Live preview: re-render when settings change after a short debounce.
  useEffect(() => {
    if (!previewBase64) return;
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(() => {
      // If we have a completed async job, re-render only (fast, no detection).
      if (completedJobId && !previewPolling) {
        rerenderPreview(completedJobId, config, token);
      } else if (!previewPolling) {
        renderPreview(previewBase64, config);
      }
    }, 400);
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
  }, [previewBase64, config.text, config.preset, config.subjectShield, config.bufferPx, config.badgeRemoval, config.opacity, config.density, renderPreview, rerenderPreview, completedJobId, previewPolling, token]);

  // Initial render once the base64 is set.
  useEffect(() => {
    if (previewBase64 && !previewUrl && !previewGenerating) {
      renderPreview(previewBase64, config);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewBase64]);

  const handleSave = useCallback(async () => {
    try {
      setError(null);
      setSuccess(false);
      setIsSaving(true);
      const res = await fetch(`${WATERMARK_API_URL}/v1/distributor/default-profile?version=${config.version}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          applyTo: config.applyTo,
          text: config.text,
          preset: config.preset,
          subjectShield: config.subjectShield,
          bufferPx: config.bufferPx,
          opacity: config.opacity,
          density: config.density,
          badgeRemoval: config.badgeRemoval,
          logoKey: config.logoKey || undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `Save failed: ${res.status}`);
      }
      const data = await res.json();
      setConfig((prev) => ({ ...prev, ...data }));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [token, config]);

  const updateField = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setSuccess(false);
  };

  const updateApplyTo = (asset, checked) => {
    setConfig((prev) => ({
      ...prev,
      applyTo: { ...prev.applyTo, [asset]: checked },
    }));
    setSuccess(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: "8px" }}>
          Watermark settings saved successfully.
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        Default Watermark Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure how ShieldShot watermarks your store preview, cover, and thumbnail images.
      </Typography>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle2" gutterBottom>
        Apply Watermark To
      </Typography>
      <FormGroup sx={{ mb: 3 }}>
        <FormControlLabel
          control={<Switch checked={config.applyTo.preview} onChange={(e) => updateApplyTo("preview", e.target.checked)} color="primary" />}
          label="Preview images"
        />
        <FormControlLabel
          control={<Switch checked={config.applyTo.cover} onChange={(e) => updateApplyTo("cover", e.target.checked)} color="primary" />}
          label="Cover images"
        />
        <FormControlLabel
          control={<Switch checked={config.applyTo.thumbnail} onChange={(e) => updateApplyTo("thumbnail", e.target.checked)} color="primary" />}
          label="Thumbnail previews"
        />
      </FormGroup>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle2" gutterBottom>
        Appearance & Behavior
      </Typography>

      <TextField
        fullWidth
        label="Watermark Text"
        value={config.text}
        onChange={(e) => updateField("text", e.target.value.slice(0, 120))}
        helperText={`${config.text.length} / 120`}
        variant="outlined"
        sx={{ mb: 2, mt: 1 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="preset-label" variant="outlined">Preset</InputLabel>
        <Select
          labelId="preset-label"
          value={config.preset}
          onChange={(e) => updateField("preset", e.target.value)}
          variant="outlined"
          label="Preset"
        >
          {PRESETS.map((p) => (
            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControlLabel
        control={<Switch checked={config.subjectShield} onChange={(e) => updateField("subjectShield", e.target.checked)} color="primary" />}
        label="Subject Shield (protects the main subject of the image)"
        sx={{ mb: 2, alignItems: "flex-start" }}
      />

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>Buffer (px)</Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <Slider
            value={config.bufferPx}
            onChange={(e, v) => updateField("bufferPx", v)}
            min={-50}
            max={256}
            aria-label="Buffer in pixels"
            valueLabelDisplay="auto"
            sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
          />
          <TextField
            label="Buffer"
            type="number"
            value={config.bufferPx}
            onChange={(e) => updateField("bufferPx", Math.min(256, Math.max(-50, parseInt(e.target.value) || 0)))}
            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
            sx={{ width: 100 }}
            size="small"
          />
        </Stack>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>Opacity</Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <Slider
            value={config.opacity}
            onChange={(e, v) => updateField("opacity", v)}
            min={0.1}
            max={1}
            step={0.05}
            aria-label="Watermark opacity"
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
            sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
          />
          <TextField
            label="Opacity"
            type="number"
            value={Math.round(config.opacity * 100)}
            onChange={(e) => {
              const pct = parseInt(e.target.value, 10);
              const safePct = Number.isFinite(pct) ? pct : 50;
              updateField("opacity", Math.min(1, Math.max(0.1, safePct / 100)));
            }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            sx={{ width: 100 }}
            size="small"
          />
        </Stack>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>Density</Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <Slider
            value={config.density}
            onChange={(e, v) => updateField("density", v)}
            min={10}
            max={50}
            step={1}
            aria-label="Watermark density"
            valueLabelDisplay="auto"
            sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}
          />
          <TextField
            label="Density"
            type="number"
            value={config.density}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              const safe = Number.isFinite(parsed) ? parsed : 25;
              updateField("density", Math.min(50, Math.max(10, safe)));
            }}
            sx={{ width: 100 }}
            size="small"
          />
        </Stack>
      </Box>

      <FormControlLabel
        control={<Switch checked={config.badgeRemoval} onChange={(e) => updateField("badgeRemoval", e.target.checked)} color="primary" />}
        label="Remove ShieldShot badge"
        sx={{ mb: 3, alignItems: "flex-start" }}
      />

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle2" gutterBottom>
        Preview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a sample image to see how your watermark will look before saving.
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<ImageIcon />}
          sx={{ borderRadius: "8px" }}
        >
          {previewUrl ? "Replace image" : "Upload preview image"}
          <input
            ref={previewInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handlePreviewFile}
          />
        </Button>
        {previewBase64 && (
          <Button variant="text" onClick={handleClearPreview} sx={{ borderRadius: "8px" }}>
            Clear
          </Button>
        )}
        {previewFileName && (
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}
            title={previewFileName}
          >
            {previewFileName}
          </Typography>
        )}
      </Stack>
      {previewBase64 && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
          {previewPolling
            ? "Subject Shield preview processing… AI is detecting the main subject."
            : config.subjectShield
              ? "Subject Shield preview may take longer (3-10 seconds) while AI detects the main subject."
              : null}
        </Typography>
      )}
      {previewBase64 && (
        <Box
          data-testid="watermark-preview-canvas"
          sx={{
            position: "relative",
            display: "inline-block",
            maxWidth: "100%",
            minWidth: 200,
            minHeight: 200,
            borderRadius: "8px",
            overflow: "hidden",
            border: 1,
            borderColor: "divider",
            bgcolor: "common.black",
            mb: 3,
          }}
        >
          {previewGenerating && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "rgba(0,0,0,0.4)", zIndex: 1 }}>
              <CircularProgress size={32} sx={{ color: "common.white" }} />
            </Box>
          )}
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Watermark preview"
              width={previewWidth || undefined}
              height={previewHeight || undefined}
              style={{ display: "block", maxWidth: "100%", maxHeight: 360, objectFit: "contain", opacity: previewGenerating ? 0.6 : 1 }}
            />
          )}
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

<Typography variant="subtitle2" gutterBottom>
        Branding
      </Typography>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ p: 2, bgcolor: "grey.50", borderRadius: "8px", border: 1, borderColor: "divider" }}
      >
        <Avatar
          variant="rounded"
          src={config.logoUrl || profileLogoUrl || undefined}
          imgProps={(config.logoUrl || profileLogoUrl) ? { alt: "Distributor logo" } : undefined}
          sx={{ width: 64, height: 64, bgcolor: (config.logoKey || profileLogoUrl) ? "primary.light" : "grey.200", color: "common.white" }}
        >
          {!(config.logoUrl || profileLogoUrl) && <ImageIcon sx={{ color: (config.logoKey || profileLogoUrl) ? "common.white" : "grey.500" }} />}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {config.logoKey ? (
            <>
              <Typography variant="body2" noWrap title={config.logoKey}>Watermark logo uploaded</Typography>
              <Typography variant="caption" color="text.secondary" noWrap title={config.logoKey} sx={{ display: "block" }}>
                {config.logoKey}
              </Typography>
            </>
          ) : profileLogoUrl ? (
            <Typography variant="body2" color="text.secondary">
              Using your Profile tab logo. Upload a dedicated watermark logo for branded overlays.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No logo uploaded yet. Manage your logo in the Profile tab.
            </Typography>
          )}
        </Box>
      </Stack>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaving}
          aria-label={isSaving ? "Saving watermark settings" : "Save watermark settings"}
          sx={{ borderRadius: "8px" }}
        >
          {isSaving ? <CircularProgress size={24} /> : "Save Changes"}
        </Button>
      </Box>
    </Box>
  );
}

function WatermarkOverlay({ text, preset, bufferPx }) {
  if (!text) return null;
  const baseStyle = {
    position: "absolute",
    pointerEvents: "none",
    color: "rgba(255,255,255,0.85)",
    textShadow: "0 1px 3px rgba(0,0,0,0.7)",
    fontFamily: "sans-serif",
    fontWeight: 600,
    whiteSpace: "nowrap",
  };
  const inset = Math.max(8, Number.isFinite(bufferPx) ? bufferPx : 24);
  if (preset === "bottom_right") {
    return (
      <Box
        data-testid="watermark-overlay-bottom-right"
        sx={{ ...baseStyle, right: inset, bottom: inset, fontSize: "1.25rem" }}
      >
        {text}
      </Box>
    );
  }
  if (preset === "tile") {
    return (
      <Box
        data-testid="watermark-overlay-tile"
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage: `repeating-linear-gradient(${Math.abs(inset)}deg, rgba(255,255,255,0.85) 0 14px, transparent 14px 90px)`,
        }}
      >
        <Box sx={{ ...baseStyle, top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: "1.5rem", opacity: 0.9 }}>
          {text}
        </Box>
      </Box>
    );
  }
  // default: clean_diagonal
  return (
    <Box
      data-testid="watermark-overlay-clean-diagonal"
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box sx={{ ...baseStyle, transform: "rotate(-30deg)", fontSize: "2rem", opacity: 0.85 }}>
        {text}
      </Box>
    </Box>
  );
}

export default SettingsWatermark;
